import * as z from "zod";

export const IconVersionSchema = z
  .string()
  .max(32)
  .regex(/^(0|[1-9]\d*)\.(0|[1-9]\d*)\.(0|[1-9]\d*)$/);
export const IconNameSchema = z
  .string()
  .max(100)
  .regex(/^[a-zA-Z][a-zA-Z0-9-]*$/);

const IconDeliveryTargetSchema = z.object({
  version: IconVersionSchema,
  name: IconNameSchema,
});
export type IconDeliveryTarget = z.infer<typeof IconDeliveryTargetSchema>;

export const IconDeliveryParamsSchema = z
  .object({ version: z.string(), fileName: z.string().endsWith(".json") })
  .transform(({ version, fileName }) => ({ version, name: fileName.slice(0, -5) }))
  .pipe(IconDeliveryTargetSchema);

// Only geometry is accepted. In particular, remote catalogs cannot introduce scripts,
// event handlers, external references, or arbitrary SVG markup.
const geometry = z.string().max(20_000);
const IconAttributesSchema = z.strictObject({
  d: geometry.optional(),
  cx: geometry.optional(),
  cy: geometry.optional(),
  r: geometry.optional(),
  x: geometry.optional(),
  y: geometry.optional(),
  x1: geometry.optional(),
  y1: geometry.optional(),
  x2: geometry.optional(),
  y2: geometry.optional(),
  rx: geometry.optional(),
  ry: geometry.optional(),
  width: geometry.optional(),
  height: geometry.optional(),
  points: geometry.optional(),
  fill: z.enum(["none", "currentColor"]).optional(),
});

export const IconNodeSchema = z
  .array(z.tuple([z.enum(["path", "circle", "ellipse", "rect", "line", "polyline", "polygon"]), IconAttributesSchema]))
  .min(1)
  .max(100);
export type IconNode = z.infer<typeof IconNodeSchema>;

export const IconCatalogSchema = z
  .record(IconNameSchema, IconNodeSchema)
  .refine((catalog) => Object.keys(catalog).length > 0, "Icon catalog must not be empty");
export type IconCatalog = z.infer<typeof IconCatalogSchema>;

export const IconDefinitionSchema = z.object({ name: IconNameSchema, node: IconNodeSchema });
export type IconDefinition = z.infer<typeof IconDefinitionSchema>;

// Null records a known invalid configured name, so hydration does not fetch it again.
export const IconDefinitionsSchema = z.record(IconNameSchema, IconDefinitionSchema.nullable());
export type IconDefinitions = z.infer<typeof IconDefinitionsSchema>;
export const IconPayloadSchema = z.object({
  version: IconVersionSchema,
  definitions: IconDefinitionsSchema,
});
export type IconPayload = z.infer<typeof IconPayloadSchema>;
